import type { PropsWithChildren, ReactNode } from 'react';
import { forwardRef, useMemo, useState } from 'react';

import { ActionIcon, Box, Group, Select, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';

import { IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { TenderTitle } from '../../../../../../../../shared/components/UI/TenderTitle/TenderTitle';

import { DecisionStatus } from '../../../../../../../../shared/entities/Interaction';
import { TenderStatus } from '../../../../../../../../shared/entities/Tender';
import { useTendersWithInteractionQuery } from './hooks/useTendersWithInteractionQuery';

export interface TenderAutoCompleteData {
  id: number;
  title: string;
  buyerId: string;
  buyerName: string;
  logoUrl?: string;
  responseDeadline?: Date;
}

interface TenderAutoCompleteProps {
  selectedTender?: TenderAutoCompleteData;
  onTenderChange?: (tender: TenderAutoCompleteData | undefined) => void;
  error?: ReactNode;
}

export function TenderAutocomplete({
  selectedTender,
  onTenderChange,
  error,
}: TenderAutoCompleteProps) {
  const { t } = useTranslation('bidResponse');
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [debouncedSearchValue] = useDebouncedValue(searchValue, 500);

  const { data } = useTendersWithInteractionQuery({
    search: debouncedSearchValue,
    status: [TenderStatus.OPEN, TenderStatus.CLOSED],
    decisionStatus: [
      DecisionStatus.TO_ANALYZE,
      DecisionStatus.GO,
      DecisionStatus.NOGO,
      DecisionStatus.ANSWERED,
      DecisionStatus.LOSS,
      DecisionStatus.WIN,
    ].join(',') as DecisionStatus,
  });

  const autocompleteData = useMemo(() => {
    return (data?.tenders || []).map(tender => ({
      value: tender.id.toString(),
      title: tender.title,
      buyerName: tender.buyer.originalName,
      logoUrl: tender.buyer.logoURL,
    }));
  }, [data]);

  function handleSearchChange(query: string) {
    setSearchValue(query);
  }

  function handleChange(value: string) {
    const tender = data?.tenders.find(tender => tender.id.toString() === value);
    if (tender) {
      onTenderChange?.({
        id: tender.id,
        title: tender.title,
        buyerId: tender.buyer.id,
        buyerName: tender.buyer.originalName,
        logoUrl: tender.buyer.logoURL,
        responseDeadline: tender?.responseDeadline
          ? new Date(tender.responseDeadline)
          : undefined,
      });
    }
  }

  function onClearSelectedTender() {
    onTenderChange?.(undefined);
  }

  if (selectedTender) {
    return (
      <FieldWrapper>
        <Group
          px="03"
          py="02"
          spacing="01"
          sx={theme => ({
            borderRadius: theme.radius.md,
            border: `1px solid ${theme.colors.gray[1]}`,
            boxShadow: theme.shadows.xs,
          })}
        >
          <Box sx={{ overflow: 'hidden', flex: 1 }}>
            <TenderTitle
              buyerOriginalName={selectedTender.buyerName}
              tenderTitle={selectedTender.title}
              logoURL={selectedTender.logoUrl}
              size="sm"
            />
          </Box>
          <ActionIcon onClick={onClearSelectedTender}>
            <IconX size={12} />
          </ActionIcon>
        </Group>
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper>
      <Select
        itemComponent={AutoCompleteItem}
        data={autocompleteData}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        maxDropdownHeight={300}
        placeholder={t(
          'project.createOrEditProject.selectTenderField.placeholder',
        )}
        searchable
        limit={100}
        filter={() => true} // filtering is done in backend
        nothingFound={t(
          'project.createOrEditProject.selectTenderField.nothingFound',
        )}
        onChange={handleChange}
        error={error}
        sx={theme => ({
          '& .mantine-Select-item': {
            padding: theme.spacing['03'],
            background: theme.white,
            borderBottom: `1px solid ${theme.colors.gray[1]}`,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            transition: 'background 0.2s ease-in-out',
            '&:hover': {
              background: theme.colors.gray[0],
            },
            '&[data-selected]': {
              background: theme.colors.primary[1],
              '&:hover': {
                background: theme.colors.primary[0],
              },
            },
            '&:last-child': {
              borderBottom: 'none',
            },
          },
        })}
      />
    </FieldWrapper>
  );
}

interface ItemProps {
  title: string;
  buyerName: string;
  logoUrl: string;
  [key: string]: any;
}

const AutoCompleteItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ title, buyerName, logoUrl, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <TenderTitle
        buyerOriginalName={buyerName}
        tenderTitle={title}
        logoURL={logoUrl}
        size="sm"
      />
    </div>
  ),
);

AutoCompleteItem.displayName = 'AutoCompleteItem';

function FieldWrapper({ children }: PropsWithChildren) {
  const { t } = useTranslation('bidResponse');
  return (
    <Stack spacing="02">
      <Text size="sm" fw={400} color="gray.6">
        {t('project.createOrEditProject.selectTenderField.description')}
      </Text>
      <Text size="sm" fw={500}>
        {t('project.createOrEditProject.selectTenderField.label')}
      </Text>
      {children}
    </Stack>
  );
}
